import {
  Alert,
  AlertIcon,
  Box,
  Center,
  Flex,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { useLoadingGif } from '@revelio/core';

import { Chart } from '../CustomDash/components/Chart/ChartRoot/Chart';
import { getEntityTypeFromPrimaryFilter } from '../CustomDash/components/EditableGrid/getEntityTypeFromPrimaryFilter';
import {
  usePageConfig,
  useTabContentConfig,
} from '../CustomDash/components/PageConfigProvider/PageConfigProvider';
import {
  GLOBAL_AI_INLINE_CHART_METRIC_PLOT_HEIGHT,
  GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT,
} from '../CustomDash/constants/panelWidths';
import { GaChartPreviewMetaProvider } from './ga-chart-preview-meta-context';
import { GlobalAIAssistantChartFilterScope } from './global-ai-assistant-chart-filter-scope';
import { useGlobalAIAssistantOptional } from './global-ai-assistant-context';
import type { GlobalAiInlineChartSpec } from './global-ai-assistant-transcript';
import { useGlobalAIAssistantFeatureEnabled } from './use-global-ai-assistant-feature-enabled';

const PREVIEW_LOADER_GIF_WIDTH = 85;
const GLOBAL_AI_ASSISTANT_TEXT_COLOR = '#2D426A';

export function GlobalAIAssistantChartPortal({
  mount,
  spec,
}: {
  mount: HTMLElement;
  spec: GlobalAiInlineChartSpec;
}) {
  const enabled = useGlobalAIAssistantFeatureEnabled();
  const gaCtx = useGlobalAIAssistantOptional();
  const { config: pageConfig } = usePageConfig();
  const { primaryFilter } = useTabContentConfig();

  const entityNow = getEntityTypeFromPrimaryFilter(primaryFilter);
  const runtime = gaCtx?.chartPreviewRuntimeByMessageId[spec.messageId];
  const chartType = spec.item.plot?.chartType;
  const secondaryDimension = spec.item.plot?.secondaryDimension;
  const dimensionSource = spec.item.plot?.dimensionSource;
  const hasBreakdownShape =
    secondaryDimension !== undefined || dimensionSource !== undefined;
  const isTextChart = chartType === 'text';
  const usesCompactMetricLayout = chartType === 'bar' && !hasBreakdownShape;
  const previewLoadingMinHeight = (() => {
    if (isTextChart) {
      return 'min-content';
    }
    if (usesCompactMetricLayout) {
      return GLOBAL_AI_INLINE_CHART_METRIC_PLOT_HEIGHT;
    }
    return GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT;
  })();
  const chartBodyMinHeight = (() => {
    if (isTextChart) {
      return 'min-content';
    }
    if (runtime?.phase === 'success') {
      return 'min-content';
    }
    return previewLoadingMinHeight;
  })();
  const loadingGif = useLoadingGif();
  const showPreviewLoadingOverlay =
    runtime?.phase !== 'success' && runtime?.phase !== 'error';

  useEffect(() => {
    if (!gaCtx) {
      return;
    }
    if (!entityNow || entityNow !== spec.entityType) {
      const blocked = `blocked: entity mismatch; expected:${spec.entityType}; current:${entityNow ?? 'none'}`;
      if (
        runtime?.phase === 'error' &&
        runtime.providerDiagnostics === blocked
      ) {
        return;
      }
      gaCtx.mergeChartPreviewRuntime(spec.messageId, {
        phase: 'error',
        providerDiagnostics: blocked,
      });
      gaCtx.addChartPreviewTimelineEvent(
        spec.messageId,
        `blocked entity mismatch (expected ${spec.entityType}, current ${entityNow ?? 'none'})`
      );
    }
  }, [gaCtx, entityNow, runtime, spec.entityType, spec.messageId]);

  if (!enabled) {
    return null;
  }

  if (!entityNow || entityNow !== spec.entityType) {
    const mismatchMessage = (
      <Text fontSize="12px" lineHeight="15px" color="gray.600">
        Chart preview will appear once this dashboard matches the answer type (
        {spec.entityType}).
      </Text>
    );
    return createPortal(
      isTextChart ? (
        mismatchMessage
      ) : (
        <Box
          py={3}
          px={2}
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          bg="gray.50"
        >
          {mismatchMessage}
        </Box>
      ),
      mount
    );
  }

  const previewErrorMessage =
    runtime?.phase === 'error'
      ? (runtime.filterApplyError ?? runtime.providerDiagnostics)
      : undefined;

  const chartPreviewBody = (
    <Box minH={chartBodyMinHeight} flexShrink={0} w="100%" position="relative">
      {previewErrorMessage ? (
        <Alert
          status="error"
          fontSize="xs"
          borderRadius={isTextChart ? 'md' : 'none'}
          flexShrink={0}
        >
          <AlertIcon />
          <Box>
            <Text fontWeight="semibold">
              {runtime?.filterApplyError
                ? 'Could not apply chart filters'
                : 'Could not render this summary'}
            </Text>
            <Text mt={1}>{previewErrorMessage}</Text>
          </Box>
        </Alert>
      ) : (
        <>
          <GlobalAIAssistantChartFilterScope
            messageId={spec.messageId}
            filters={spec.filters}
            pageId={pageConfig.id}
            revelioPageId={pageConfig.revelioPageId}
            primaryFilterId={primaryFilter?.id}
            primaryFilterSelectionLimit={1}
            primaryFilterSelectionDefault={1}
            loadingMinHeight={previewLoadingMinHeight}
            compactLoading={isTextChart}
          >
            <GaChartPreviewMetaProvider
              messageId={spec.messageId}
              itemId={spec.item.id}
            >
              <Chart
                item={spec.item}
                primaryFilter={primaryFilter}
                editMode={false}
              />
            </GaChartPreviewMetaProvider>
          </GlobalAIAssistantChartFilterScope>
          {showPreviewLoadingOverlay && isTextChart ? (
            <Flex align="center" gap={2} py={1}>
              <Spinner size="sm" color={GLOBAL_AI_ASSISTANT_TEXT_COLOR} />
              <Text
                fontSize="12px"
                lineHeight="15px"
                color={GLOBAL_AI_ASSISTANT_TEXT_COLOR}
                opacity={0.8}
              >
                Writing summary…
              </Text>
            </Flex>
          ) : null}
        </>
      )}
      {showPreviewLoadingOverlay && !isTextChart ? (
        <Center
          position="absolute"
          inset={0}
          bg="white"
          borderRadius="md"
          zIndex={3}
          pointerEvents="none"
          data-testid={`global-ai-assistant-chart-preview-loading-${spec.messageId}`}
        >
          {loadingGif ? (
            <img
              src={loadingGif}
              alt="Loading chart"
              width={PREVIEW_LOADER_GIF_WIDTH}
              style={{
                display: 'block',
                width: `${PREVIEW_LOADER_GIF_WIDTH}px`,
                height: 'auto',
                mixBlendMode: 'multiply',
              }}
            />
          ) : (
            <Spinner
              thickness="4px"
              speed="0.7s"
              color="green.500"
              emptyColor="gray.200"
              size="md"
            />
          )}
        </Center>
      ) : null}
    </Box>
  );

  if (isTextChart) {
    return createPortal(
      <Box w="100%" display="flex" flexDirection="column" flexShrink={0}>
        {chartPreviewBody}
      </Box>,
      mount
    );
  }

  return createPortal(
    <Box
      w="100%"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      overflow="visible"
      bg="white"
      display="flex"
      flexDirection="column"
      flexShrink={0}
    >
      {chartPreviewBody}
      {spec.chartSubtitle ? (
        <Text
          px={2}
          py={1.5}
          fontSize="xs"
          color="gray.600"
          borderTopWidth="1px"
          borderColor="gray.100"
          flexShrink={0}
          lineHeight="short"
        >
          {spec.chartSubtitle}
        </Text>
      ) : null}
    </Box>,
    mount
  );
}
